class PersonalizationModalDialog extends ModalDialog {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.submitButton = this.form.querySelector('button[type="submit"]');

    this.variantId = this.dataset.variantId;
    this.productTypeMatch = this.dataset.productTypeMatch;
    this.requiredProperties = [];

    subscribe(PUB_SUB_EVENTS.cartAdd, this.handleCartAdd.bind(this));
    subscribe(PUB_SUB_EVENTS.cartRender, ({ cart }) => {
      this.cart = cart;
    });

    this.form.addEventListener("submit", this.handleSubmit.bind(this));

    this.removeButton = this.querySelector(".js-personalization-remove");
    if (this.removeButton) {
      this.removeButton.addEventListener(
        "click",
        this.removePersonalization.bind(this)
      );
    }
  }

  getCart() {
    if (!this.cart) {
      this.cart = (window.BIRTHDATE_CART &&
        window.BIRTHDATE_CART.getCartSync()) || {
        items: [],
      };
    }
    return this.cart;
  }

  getFocusTarget() {
    return (
      this.querySelector('input:not([type="hidden"])') ||
      this.closeButtons[0] ||
      super.getFocusTarget()
    );
  }

  compareItems(obj1, obj2) {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

    // Check each item in the object
    for (let key in obj1) {
      if (Object.prototype.hasOwnProperty.call(obj1, key)) {
        if (obj1[key] !== obj2[key]) return false;
      }
    }

    // If no errors, return true
    return true;
  }
  findCartAddMatch(items) {
    return items.find((item) => item.product_type === this.productTypeMatch);
  }
  handleCartAdd({ cart, addedItems }) {
    this.cart = cart;
    const addedAddOn = !!addedItems.find(
      (item) => item.variant_id === this.variantId
    );
    if (addedAddOn) {
      return;
    }
    const cartItems = cart.items;
    const cartAddedItems = addedItems.map((item) => {
      if (item.product_type) {
        return item;
      }
      const itemMatch = cartItems.find((i) => item.key === i.key);
      if (!itemMatch) {
        const variantMatch = cartItems.find(
          (i) =>
            i.variant_id === item.variant_id &&
            i.quantity === item.quantity &&
            this.compareItems(i.properties || {}, item.properties || {})
        );
        if (variantMatch) {
          return variantMatch;
        }
      }
      return item;
    });

    const matchingItem = this.findCartAddMatch(cartAddedItems);
    if (matchingItem) {
      setTimeout(() => {
        // Delay this slightly so cart has a chance to open first.
        this.showForLineItem(matchingItem.key);
      }, 10);
    }
  }

  serializeForm() {
    const data = new FormData(this.form);
    const obj = {};
    for (let [key, value] of data) {
      if (obj[key] !== undefined) {
        if (!Array.isArray(obj[key])) {
          obj[key] = [obj[key]];
        }
        obj[key].push(value);
      } else {
        obj[key] = value;
      }
    }
    return obj;
  }

  hasPersonalization(data = {}) {
    return !this.requiredProperties.find((key) => !(data[key] || "").trim());
  }

  generateUniqueId() {
    return "_" + Math.random().toString(36).substring(2, 9);
  }

  updatePersonalization(data) {
    if (this.isSubmitting || !this.editingItem) {
      return;
    }

    if (!this.hasPersonalization(data)) {
      this.removePersonalization();
      return;
    }

    this.setSubmitState(true);

    window.BIRTHDATE_CART.changeCart({
      id: this.editingItem.key,
      quantity: this.editingItem.quantity,
      properties: {
        ...(this.editingItem.properties || {}),
        ...data,
      },
    }).finally(() => {
      this.hide();
      this.setSubmitState(false);
    });
  }

  removeAddOn(item) {
    const properties = {
      _id: this.generateUniqueId(),
      ...(item.properties || {}),
    };
    this.requiredProperties.forEach((key) => {
      properties[key] = undefined;
    });
    return window.BIRTHDATE_CART.changeCart({
      id: item.key,
      quantity: item.quantity,
      properties,
    });
  }

  removePersonalization() {
    if (this.isSubmitting) {
      return;
    }
    if (!this.editingItem) {
      this.hide();
      return;
    }

    this.setSubmitState(true);

    return this.removeAddOn(this.editingItem).finally(() => {
      this.hide();
      this.setSubmitState(false);
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    if (this.isSubmitting || !this.editingItem) {
      return;
    }

    const formData = this.serializeForm();
    if (this.hasPersonalization(formData)) {
      this.updatePersonalization(formData);
    } else {
      this.removePersonalization();
    }
  }

  setSubmitState(submitting) {
    this.isSubmitting = submitting;
    this.submitButton.disabled = submitting;
  }

  render() {}

  getLineItem(key) {
    return this.getCart().items.find((i) => i.key === key) || null;
  }

  showForLineItem(key) {
    this.editingItem = this.getLineItem(key);

    if (this.editingItem) {
      this.show();
    }
  }

  show(opener) {
    if (opener && opener.dataset.key) {
      const itemKey = opener.dataset.key;
      this.editingItem =
        this.getCart().items.find((i) => i.key === itemKey) || null;
    }
    if (this.editingItem) {
      this.render();
    }

    super.show(opener);
  }
}

class PendantEngravingDialog extends PersonalizationModalDialog {
  constructor() {
    super();
    this.requiredProperties = ["Engraving"];
    this.productTypeMatch = "Pendant";

    this.input = this.form.querySelector('input[name="Engraving"]');
    this.characterCount = this.querySelector(".js-character-count");

    if (this.input) {
      this.input.addEventListener("input", (e) => {
        this.handleEngravingChange(e.target.value);
      });

      this.input.addEventListener("focus", (e) => {
        this.input.scrollIntoView({
          block: "end",
          inline: "nearest",
        });
      });
    }
  }

  get previewContainer() {
    return (
      this._previewContainer ||
      (this._previewContainer = this.querySelector(".js-preview-container"))
    );
  }
  get initialsElement() {
    return (
      this._initialsElement ||
      (this._initialsElement = this.querySelector(
        ".js-engraving-preview-initials"
      ))
    );
  }
  get dateElement() {
    return (
      this._dateElement ||
      (this._dateElement = this.querySelector(".js-engraving-preview-date"))
    );
  }
  get zodiacElement() {
    return (
      this._zodiacElement ||
      (this._zodiacElement = this.querySelector(".js-engraving-preview-zodiac"))
    );
  }
  get engravingContainer() {
    return (
      this._engravingContainer ||
      (this._engravingContainer = this.querySelector(".js-engraving-container"))
    );
  }

  handleEngravingChange(newValue) {
    if (!this.input) {
      return;
    }
    let value = newValue.trim().replace(/[^A-Za-z]/g, "");
    if (value.length > 3) {
      value = value.substring(0, 3);
    }
    if (value !== this.input.value) {
      this.input.value = value;
    }
    if (this.characterCount) {
      this.characterCount.textContent = `${value.length}/3`;
    }

    if (this.initialsElement) {
      this.initialsElement.innerHTML = value
        ? value.toUpperCase().split("").join(".") + "."
        : "M.S.P.";
    }
  }

  serializeForm() {
    const data = super.serializeForm();
    data.Engraving = (data.Engraving || "")
      .trim()
      .substring(0, 3)
      .toUpperCase();
    return data;
  }

  show(opener) {
    super.show(opener);

    this.engravingContainer.style.display = "none";

    fetch(
      `${window.routes.root_url}${
        window.routes.root_url.endsWith("/") ? "" : "/"
      }products/${this.editingItem.handle}?section_id=${this.dataset.sectionId}`
    )
      .then((res) => res.text())
      .then((res) => {
        const html = new DOMParser().parseFromString(res, "text/html");
        const dateSource = html.querySelector(".js-engraving-preview-date");
        const zodiacSource = html.querySelector(".js-engraving-preview-zodiac");
        if (dateSource) {
          this.dateElement.innerHTML = dateSource.innerHTML;
        }
        if (zodiacSource) {
          this.zodiacElement.innerHTML = zodiacSource.innerHTML;
        }

        this.engravingContainer.classList.add("animate-fade-in-fast");
        this.engravingContainer.style.display = "block";
      });
  }

  render() {
    const properties = this.editingItem.properties || {};
    const engraving = (properties.Engraving || "").trim().toUpperCase();

    // Require input value unless we're editing
    // an engraving, in which case submitting
    // an empty input should remove the engraving
    this.input.required = !engraving;
    this.handleEngravingChange(engraving);

    if (!!engraving) {
      this.classList.remove("personalization-modal--adding");
      this.classList.add("personalization-modal--editing");
    } else {
      this.classList.remove("personalization-modal--editing");
      this.classList.add("personalization-modal--adding");
    }

    const materialOption = this.editingItem.variant_title || "14k Gold Plated";

    this.previewContainer.setAttribute("data-material", materialOption);
  }
}
customElements.define("pendant-engraving-dialog", PendantEngravingDialog);

class BirthdateCandlePersonalizationDialog extends PersonalizationModalDialog {
  constructor() {
    super();

    this.previewDefaultName = this.dataset.previewDefaultName;
    this.input = this.querySelector('input[name="Name"]');
    this.previewNameSpacer = this.querySelector(".js-preview-name-spacer");
    this.previewName = this.querySelector(".js-preview-name");
    this.previewDate = this.querySelector(".js-preview-date");
    this.requiredProperties = ["Name"];
    this.productTypeMatch = "Candle";

    this.characterCount = this.querySelector(".js-character-count");
    if (this.input) {
      this.setSelectionRange = this.setSelectionRange.bind(this);
      this.input.addEventListener("focus", this.setSelectionRange);
      this.input.addEventListener("click", this.setSelectionRange);
      this.input.addEventListener("keydown", this.setSelectionRange);
      this.input.addEventListener("input", (e) => {
        this.setPreviewName(e.target.value);
      });

      this.input.addEventListener("focus", (e) => {
        this.input.scrollIntoView({
          block: "end",
          inline: "nearest",
        });
      });
    }
  }

  get charLimit() {
    return parseInt(this.input.maxLength, 10) || 25;
  }

  setSelectionRange(e) {
    if (!this.selectionRange) {
      this.selectionRange = {};
    }
    this.selectionRange.start = this.input.selectionStart;
    this.selectionRange.end = this.input.selectionEnd;
  }

  cleanName(name) {
    return name
      .replace(/\s+/g, " ")
      .replace(/[^ \p{Script=Latin}\p{S}\p{N}\p{P}]/gu, "")
      .substring(0, this.charLimit);
  }

  formatName(value) {
    return value.trim() || this.previewDefaultName;
  }

  setPreviewName(name = "") {
    const value = this.cleanName(name);
    this.input.value = value;

    this.nameValue = value;
    if (value.length) {
      this.input.classList.remove("text-accent", "uppercase");
    } else {
      this.input.classList.add("text-accent", "uppercase");
    }
    if (this.characterCount) {
      this.characterCount.textContent = `${value.length}/${this.charLimit}`;
    }

    this.previewName.textContent = this.formatName(value);

    return value;
  }

  serializeForm() {
    const data = super.serializeForm();
    data.Name = this.cleanName(data.Name || "").trim();
    return data;
  }

  setBackgroundImage() {
    if (!this.editingItem.handle) {
      return;
    }

    const targetImage = this.querySelector(".js-label-image");
    if (!targetImage) {
      return;
    }
    targetImage.classList.remove("transition-opacity");
    targetImage.classList.add("opacity-0");

    targetImage.addEventListener(
      "load",
      () => {
        targetImage.classList.add("transition-opacity");
        targetImage.classList.remove("opacity-0");
      },
      { once: true }
    );

    targetImage.src = `/cdn/shop/files/${this.editingItem.handle}-blank-label.svg`;
  }

  render() {
    this.setBackgroundImage();

    const name = this.setPreviewName(
      this.editingItem.properties ? this.editingItem.properties.Name : undefined
    );
    this.input.required = !name;

    if (!!name) {
      this.classList.remove("personalization-modal--adding");
      this.classList.add("personalization-modal--editing");
    } else {
      this.classList.remove("personalization-modal--editing");
      this.classList.add("personalization-modal--adding");
    }
  }
}
customElements.define(
  "birthdate-candle-personalization-dialog",
  BirthdateCandlePersonalizationDialog
);

class WishCandlePersonalizationDialog extends BirthdateCandlePersonalizationDialog {
  constructor() {
    super();
    this.productTypeMatch = "Wish Candle";
  }

  setBackgroundImage() {
    const variantName = this.editingItem.options_with_values
      .map(({ value }) => value)
      .join(" / ");

    const labels = this.querySelectorAll(".js-label");
    const colors = {};
    labels.forEach((label) => {
      if (label.dataset.name === variantName) {
        label.classList.remove("hidden");
        colors.candleColor = label.dataset.candleColor;
        colors.textColor = label.dataset.textColor;
      } else {
        label.classList.add("hidden");
      }
    });

    const illustration = this.querySelector(".js-candle-illustration");
    if (illustration) {
      illustration.style.color = colors.candleColor;
    }
    this.querySelector(".js-preview-container").style.color = colors.textColor;
  }

  formatName(value) {
    return "for " + (value.trim() || this.previewDefaultName);
  }
}
customElements.define(
  "wish-candle-personalization-dialog",
  WishCandlePersonalizationDialog
);

class BookGiftWrapDialog extends PersonalizationModalDialog {
  constructor() {
    super();
    this.requiredProperties = ["Gift Wrap"];
    this.productTypeMatch = "Book";
    this.matchProducts = this.dataset.matchProducts
      ? this.dataset.matchProducts.split(",")
      : [];
  }

  serializeForm() {
    const data = super.serializeForm();
    data["Gift Wrap"] = "Yes";
    return data;
  }

  render() {
    super.render();
    const giftWrapped = this.editingItem.properties
      ? this.editingItem.properties["Gift Wrap"]
      : undefined;

    if (!!giftWrapped) {
      this.classList.remove("personalization-modal--adding");
      this.classList.add("personalization-modal--editing");
    } else {
      this.classList.remove("personalization-modal--editing");
      this.classList.add("personalization-modal--adding");
    }
  }

  findCartAddMatch(items) {
    return items.find((item) => {
      if (item.properties && item.properties._bundle) {
        return false;
      }
      return this.matchProducts.includes(item.handle);
    });
  }

  show(opener) {
    super.show(opener);

    if (this.editingItem) {
      this.setAttribute("data-active-product", this.editingItem.handle);
    }
  }
}
customElements.define("book-gift-wrap-dialog", BookGiftWrapDialog);
